"use client";

import Mentor from "../../Library/Mentor/Mentor";
import TranslateDomain from "@/components/TranslateDomain/TranslateDomain";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Link } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

export default function Mentoring({ mentors: tmpMentors }) {
    const t = useTranslations();

    const [mentors] = useState(tmpMentors);
    const [filteredMentors, setFilteredMentors] = useState(tmpMentors);
    const [selectedDomains, setSelectedDomains] = useState(["all"]);

    const onDomainSelectedHandler = (domainColumn) => {
        if (domainColumn === "all") {
            setSelectedDomains(["all"]);
            return;
        }
        let updated = selectedDomains.filter((d) => d !== "all");
        if (updated.includes(domainColumn)) {
            updated = updated.filter((d) => d !== domainColumn);
        } else {
            updated = [...updated, domainColumn];
        }
        setSelectedDomains(updated.length === 0 ? ["all"] : updated);
    };

    useEffect(() => {
        // Filtre en OU + tri par pertinence (les mentors couvrant le plus de
        // domaines sélectionnés remontent en premier).
        if (selectedDomains.length > 0 && !selectedDomains.includes("all")) {
            const matchCount = (m) =>
                selectedDomains.reduce((c, d) => c + (m[d] ? 1 : 0), 0);
            setFilteredMentors(
                [...mentors]
                    .filter((m) => matchCount(m) > 0)
                    .sort((a, b) => matchCount(b) - matchCount(a)),
            );
        } else {
            setFilteredMentors(mentors);
        }
    }, [selectedDomains, mentors]);

    // Un filtre qui ne mène à personne n'est pas affiché : cliquer dessus pour
    // tomber sur « aucun mentor trouvé » est le pire signal que puisse envoyer
    // un annuaire. En dessous de deux domaines couverts, la barre entière n'a
    // plus rien à trier.
    const availableDomains = (process.env.MENTOR_DOMAINS || []).filter((d) =>
        mentors.some((mentor) => mentor[d.domainColumn]),
    );

    // La grille suit le nombre de cartes : sur quatre colonnes, une petite
    // équipe laisse une ligne à moitié vide. Sous neuf cartes (les mentors plus
    // l'invitation à candidater), on reste à trois colonnes, plus grandes.
    const gridColumns =
        filteredMentors.length + 1 >= 9
            ? "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            : "sm:grid-cols-2 lg:grid-cols-3";

    const pillClass = (active) =>
        `text-sm px-4 py-2 rounded-full font-medium border transition-colors ${
            active
                ? "bg-primary text-white border-primary"
                : "border-gray-200 dark:border-borderDark text-gray-700 dark:text-grayDark-300 hover:border-gray-400 dark:hover:border-grayDark-500"
        }`;

    return (
        <>
            {/* Filtres */}
            {availableDomains.length > 1 && (
                <div className="flex flex-wrap gap-2.5 mt-8 mb-10">
                    <button
                        type="button"
                        onClick={() => onDomainSelectedHandler("all")}
                        className={pillClass(selectedDomains.includes("all"))}
                    >
                        <TranslateDomain domain="Tout" />
                    </button>
                    {availableDomains.map((d) => (
                        <button
                            key={d.domainColumn}
                            type="button"
                            onClick={() => onDomainSelectedHandler(d.domainColumn)}
                            className={pillClass(
                                selectedDomains.includes(d.domainColumn),
                            )}
                        >
                            <TranslateDomain domain={d.domainName} />
                        </button>
                    ))}
                </div>
            )}

            {/* Alerte aucun résultat */}
            {filteredMentors.length === 0 && (
                <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200/50 dark:border-amber-500/20 rounded-2xl p-5 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 text-amber-600 dark:text-amber-400"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                                />
                            </svg>
                        </div>
                        <p className="text-amber-800 dark:text-amber-300 text-sm font-medium">
                            {t("mentoring_noMentorsFoundAlert")}
                        </p>
                    </div>
                </div>
            )}

            {/* Mentors + invitation à devenir mentor */}
            <div
                className={`grid grid-cols-1 ${gridColumns} gap-5 ${
                    availableDomains.length > 1 ? "" : "mt-8"
                }`}
            >
                {filteredMentors.map((mentor) => (
                    <Mentor mentor={mentor} key={mentor.id} />
                ))}

                {/* Devenir mentor */}
                <Link
                    href="/join-mentors"
                    className="group aspect-[3/4] rounded-2xl overflow-hidden flex flex-col items-center justify-center text-center p-6 border-2 border-dashed border-white/15 hover:border-primary/60 transition-colors"
                    style={{
                        background:
                            "linear-gradient(155deg, #1c1f27 0%, #0d0e12 100%)",
                    }}
                >
                    <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center text-primary text-2xl mb-4">
                        <SolarIcon name="user-plus-bold" />
                    </div>
                    <div className="text-lg font-bold text-white font-sourceSans">
                        {t("mentoring_become_mentor_title")}
                    </div>
                    <p className="text-sm text-white/55 mt-2 max-w-[220px] leading-relaxed">
                        {t("mentoring_become_mentor_text")}
                    </p>
                    <span className="mt-4 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg">
                        {t("mentoring_become_mentor_cta")}
                    </span>
                </Link>
            </div>
        </>
    );
}
